import { encodeFunctionData, Hex, parseEventLogs, sha256 } from 'viem';
import {
  PORTAL_METADATA_IPFS_HASH,
  REGISTRY_CONTRACT_ADDRESS,
} from './constants';

import {
  exportKeyPair,
  generateRandomRSAKeyPair,
  generateRandomUcanEdKeyPair,
} from './crypto-utils';
import { TGunUserNode, IPortal, FileTypeEnum } from '@/types';
import {
  CONTRACT_ABI_MAP,
  ContractMethod,
  ContractType,
  PortalContractEvent,
} from './contract-utils';
import { getGunPortalKeys, isNullish, jsonToBase64, Sea } from './gun-utils';
import { addPortal, getPortalByOwner, modifyUserCacheData } from '@/db/queries';
import { fromUint8Array } from 'js-base64';
import { captureException } from '@sentry/nextjs';
import { AgentInstance } from './identity-utils/smart-account-agent';
import {
  FAILED_USER_OP_STATUSES,
  FILE_TRX_TIMEOUT,
  getUserOpStatus,
  PORTAL_MINT_TIMEOUT,
  waitForUserOpReceipt,
} from './account-utils';
import { UserCache } from '@/db/db';
import { resolveReceiptAndParseLog } from './file-utils';

const generateRandomISEAKey = async () => {
  const seaPair = await Sea.pair();
  return jsonToBase64(seaPair);
};

export const generatePortalKeys = async () => {
  const editKeyPair = await generateRandomUcanEdKeyPair();
  const viewKeyPair = await generateRandomUcanEdKeyPair();
  const editSecret = await editKeyPair.export();
  const viewSecret = await viewKeyPair.export();
  const editDid = editKeyPair.did();
  const viewDid = viewKeyPair.did();
  // serverKeys
  const {
    publicKey: collaboratorPublicKey,
    privateKey: collaboratorPrivateKey,
  } = await exportKeyPair(await generateRandomRSAKeyPair());
  // memberKeys
  const { publicKey: memberPublicKey, privateKey: memberPrivateKey } =
    await exportKeyPair(await generateRandomRSAKeyPair());
  // ownerKeys
  const { publicKey: ownerPublicKey, privateKey: ownerPrivateKey } =
    await exportKeyPair(await generateRandomRSAKeyPair());

  return {
    viewSecret,
    editSecret,
    editDID: editDid,
    viewDID: viewDid,
    editKeyPair,
    portalGunKey: await generateRandomISEAKey(),
    ownerEncryptionKey: fromUint8Array(new Uint8Array(ownerPublicKey)),
    ownerDecryptionKey: fromUint8Array(new Uint8Array(ownerPrivateKey)),
    memberEncryptionKey: fromUint8Array(new Uint8Array(memberPublicKey)),
    memberDecryptionKey: fromUint8Array(new Uint8Array(memberPrivateKey)),
    portalEncryptionKey: fromUint8Array(new Uint8Array(collaboratorPublicKey)),
    portalDecryptionKey: fromUint8Array(new Uint8Array(collaboratorPrivateKey)),
  };
};

const getPortalKeyVerifiers = (secretFileContent: any) => {
  const encoder = new TextEncoder();

  return {
    portalEncryptionKeyVerifier: sha256(
      encoder.encode(secretFileContent.portalEncryptionKey)
    ),
    portalDecryptionKeyVerifier: sha256(
      encoder.encode(secretFileContent.portalDecryptionKey)
    ),
    memberEncryptionKeyVerifer: sha256(
      encoder.encode(secretFileContent.memberEncryptionKey)
    ),
    memberDecryptionKeyVerifer: sha256(
      encoder.encode(secretFileContent.memberDecryptionKey)
    ),
  };
};

export const mintPortal = async () => {
  try {
    const portalKeys = await generatePortalKeys();
    const keyVerifiers = getPortalKeyVerifiers(portalKeys);

    const encodedCallData = encodeFunctionData({
      abi: CONTRACT_ABI_MAP[ContractType.Registry],
      functionName: ContractMethod.Mint,
      args: [
        PORTAL_METADATA_IPFS_HASH,
        portalKeys.viewDID,
        portalKeys.editDID,
        keyVerifiers.portalEncryptionKeyVerifier.valueOf(),
        keyVerifiers.portalDecryptionKeyVerifier.valueOf(),
        keyVerifiers.memberEncryptionKeyVerifer.valueOf(),
        keyVerifiers.memberDecryptionKeyVerifer.valueOf(),
      ],
    });

    const { logs } = await AgentInstance.executeUserOperationRequest(
      {
        data: encodedCallData,
        contractAddress: REGISTRY_CONTRACT_ADDRESS,
      },
      PORTAL_MINT_TIMEOUT
    );

    const parsedLog = parseEventLogs({
      abi: CONTRACT_ABI_MAP[ContractType.Registry],
      eventName: PortalContractEvent.Mint,
      logs,
    });
    if (parsedLog.length === 0) throw new Error('No Portal Mint Event Found!');

    // @ts-ignore
    const creds = {
      owner: AgentInstance.getAgentAddress(),
      // @ts-ignore
      portalAddress: parsedLog[0]?.args?.portal,
      credentials: {
        editDID: portalKeys.editDID,
        editSecret: portalKeys.editSecret,
        viewDID: portalKeys.viewDID,
        viewSecret: portalKeys.viewSecret,
      },
      serverKeys: {
        memberDecryptionKey: portalKeys.memberDecryptionKey,
        memberEncryptionKey: portalKeys.memberEncryptionKey,
        ownerDecryptionKey: portalKeys.ownerDecryptionKey,
        ownerEncryptionKey: portalKeys.ownerEncryptionKey,
        portalDecryptionKey: portalKeys.portalDecryptionKey,
        portalEncryptionKey: portalKeys.portalEncryptionKey,
        portalGunKey: portalKeys.portalGunKey,
      },
    };
    return creds;
  } catch (err) {
    console.error(err);
    captureException(err);
    throw err;
  }
};

export const getLocalPortalKeys = async (
  legacySmartAccountAddress: Hex,
  userNode?: TGunUserNode
): Promise<IPortal | null> => {
  const existingPortalOnIdb = await getPortalByOwner(legacySmartAccountAddress);
  // return keys if found in idb
  if (existingPortalOnIdb) return existingPortalOnIdb;

  // get keys from gun if not found in idb
  if (userNode) {
    const existingPortalOnGun = await getGunPortalKeys(userNode);

    if (existingPortalOnGun) {
      // save to idb
      await addPortal(existingPortalOnGun);
      return existingPortalOnGun;
    }
  }

  return null;
};

interface AddFileTransactionArgs {
  contractAddress: string;
  contentIpfsHash: string;
  metadataIpfsHash: string;
  fileType: FileTypeEnum;
  gateIpfsHash?: string;
  version?: number;
}

export const addFileTransaction = async (args: AddFileTransactionArgs) => {
  const callData = encodeFunctionData({
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    functionName: ContractMethod.AddFile,
    args: [
      args.metadataIpfsHash,
      args.contentIpfsHash,
      args.gateIpfsHash || '',
      args.fileType,
      args.version || 0,
    ],
  });

  const txHash = await AgentInstance.sendUserOperation({
    data: callData,
    contractAddress: args.contractAddress as Hex,
  });

  return txHash;
};

interface IEditFileTransactionArgs extends AddFileTransactionArgs {
  fileId: number;
}

export const editFileTransaction = async (args: IEditFileTransactionArgs) => {
  const callData = encodeFunctionData({
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    functionName: ContractMethod.EditFile,
    args: [
      args.fileId,
      args.metadataIpfsHash,
      args.contentIpfsHash,
      args.gateIpfsHash || '',
      args.fileType,
      args.version || 0,
    ],
  });

  const txHash = await AgentInstance.sendUserOperation({
    data: callData,
    contractAddress: args.contractAddress as Hex,
  });

  return txHash;
};

interface AddOrEditFileTransactionArgs
  extends AddFileTransactionArgs,
    IEditFileTransactionArgs {}

export const addOrEditFileTransaction = async (
  args: AddOrEditFileTransactionArgs
) => {
  if (!isNullish(args.fileId)) {
    return editFileTransaction(args);
  }
  return addFileTransaction(args);
};

const DELETE_FILE_METADATA = {
  metadataIpfsHash: 'QmVUt7LBmDeuL4mxHYrT9bXETAWmMBVPLHtefC2uvrbWnA',
  contentIpfsHash: 'QmQq1cBS2hzigkSTgrRKbrBnWhUTsHWNmPmzjaFiQSGEo1',
};

export const deleteFileTransaction = async (
  docItem: UserCache,
  walletAddress: string
) => {
  if (docItem.pendingTxnHash) {
    const status = await getUserOpStatus(docItem.pendingTxnHash);

    if (FAILED_USER_OP_STATUSES.includes(status)) {
      await modifyUserCacheData(docItem.ddocId, walletAddress, {
        pendingTxnHash: null,
      });
    } else {
      const parsedLog = await resolveReceiptAndParseLog(
        docItem.pendingTxnHash,
        PortalContractEvent.EditedFile,
        docItem.ddocId,
        walletAddress,
        FILE_TRX_TIMEOUT
      );
      if (parsedLog.length > 0) {
        //@ts-ignore
        return parsedLog[0].args.fileId;
      }
    }
  }

  const txHash = await editFileTransaction({
    ...DELETE_FILE_METADATA,
    contractAddress: docItem.portalAddress,
    fileId: docItem.onChainFileId as number,
    fileType: FileTypeEnum.PRIVATE,
    version: 0,
  });

  await modifyUserCacheData(docItem.ddocId, docItem.address, {
    pendingTxnHash: txHash,
  });

  const txReceipt = await waitForUserOpReceipt(txHash, FILE_TRX_TIMEOUT);
  const parsedLog = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    eventName: PortalContractEvent.EditedFile,
    logs: txReceipt.logs,
  });

  if (parsedLog.length === 0) throw new Error('No Edited File Event Found!');

  await modifyUserCacheData(docItem.ddocId, docItem.address, {
    pendingTxnHash: null,
  });

  // @ts-ignore
  return parsedLog[0].args.fileId;
};
